import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import type { OptionGroup, ValueEditorProps } from 'react-querybuilder';

import {
  formatDate,
  CommaSeparatedSupportedLabels,
  InputType,
  getOptionByName,
  formatDateISO,
} from '../helper';

interface InputProps {
  value: any;
  index: number;
  fieldData: any;
  valueEditor: any;
  disabled?: boolean;
  handleOnChange: (value: any) => void;
}

const NumericInput: React.FC<InputProps> = ({
  index,
  value,
  fieldData,
  valueEditor,
  handleOnChange,
  disabled = false,
}) => {
  let actualValue = value?.value?.split(' ,');
  return (
    <div className="custom-value-editor-container">
      <TextField
        className="custom-value-editor-textfield"
        type="number"
        size="small"
        value={actualValue ? actualValue[index] : ''}
        onChange={(event: any) => {
          if (actualValue) {
            actualValue[index] = event?.target?.value;
          } else {
            actualValue = [event?.target?.value];
          }
          const newValue = {
            type: fieldData.dataType,
            data: {
              attributeType: fieldData.attributeType,
            },
            value: actualValue.join(' ,'),
          };
          handleOnChange(newValue);
        }}
        inputProps={{
          min: valueEditor.minValue,
          max: valueEditor.maxValue,
        }}
        disabled={disabled}
      />
    </div>
  );
};

const DateInput: React.FC<InputProps> = ({
  index,
  value,
  fieldData,
  handleOnChange,
  disabled = false,
}) => {
  let actualValue = value?.value?.split(' ,');
  return (
    <div className="custom-value-editor-container">
      <TextField
        className="custom-value-editor-textfield"
        type="datetime-local"
        size="small"
        value={actualValue ? formatDate(new Date(actualValue[index])) : ''}
        onChange={(event: any) => {
          if (actualValue) {
            actualValue[index] = event.target.value;
          } else {
            actualValue = [event.target.value];
          }
          actualValue = actualValue.map((item) =>
            formatDateISO(new Date(item))
          );

          const newValue = {
            type: fieldData.dataType,
            data: {
              attributeType: fieldData.attributeType,
            },
            value: actualValue.join(' ,'),
          };
          handleOnChange(newValue);
        }}
        disabled={disabled}
      />
    </div>
  );
};

const SelectInput: React.FC<InputProps> = ({
  index,
  value,
  fieldData,
  valueEditor,
  handleOnChange,
  disabled = false,
}) => {
  let actualValue = value?.value?.split(' ,');
  return (
    <div className="custom-value-editor-container">
      <Autocomplete
        disablePortal
        id="combo-box-demo"
        options={valueEditor.values}
        size="small"
        className="custom-value-editor-autocomplete"
        sx={{ width: 300 }}
        renderInput={(params) => <TextField {...params} />}
        onChange={(event: any, value: any) => {
          if (actualValue) {
            actualValue[index] = value?.value;
          } else {
            actualValue = [value?.value];
          }
          const newValue = {
            type: fieldData.dataType,
            data: {
              attributeType: fieldData.attributeType,
            },
            value: actualValue.join(' ,'),
          };
          handleOnChange(newValue);
        }}
        value={
          valueEditor?.values.find((item) => item?.value === value?.value) ||
          null
        }
        disabled={disabled}
      />
    </div>
  );
};

const TextInput: React.FC<InputProps> = ({
  index,
  value,
  fieldData,
  handleOnChange,
  disabled = false,
}) => {
  let actualValue = value?.value?.split(' ,');
  return (
    <div className="custom-value-editor-container">
      <TextField
        className="custom-value-editor-textfield"
        size="small"
        value={actualValue ? actualValue[index] : ''}
        onChange={(event: any) => {
          if (actualValue) {
            actualValue[index] = event.target.value;
          } else {
            actualValue = [event.target.value];
          }
          const newValue = {
            type: fieldData.dataType,
            data: {
              attributeType: fieldData.attributeType,
            },
            value: actualValue.join(' ,'),
          };
          handleOnChange(newValue);
        }}
        disabled={disabled}
      />
    </div>
  );
};

const CustomValueEditor: React.FC<ValueEditorProps> = ({
  value,
  operator,
  disabled,
  fieldData,
  handleOnChange,
}) => {
  const [valueEditor, setValueEditor] = useState<any>();

  useEffect(() => {
    const valueEditorFound = getOptionByName(
      (fieldData.operators as OptionGroup[]) ?? [],
      operator
    );
    setValueEditor(valueEditorFound);
  }, [fieldData.operators, operator]);

  return (
    <React.Fragment>
      {valueEditor && valueEditor.valueEditorType.length > 0 ? (
        (() => {
          const dataType = (fieldData as any)?.dataType?.toUpperCase();

          const el =
            valueEditor.valueEditorType.find(
              (type) => type.toUpperCase() === dataType
            ) || valueEditor.valueEditorType[0];

          if (el.toUpperCase() === InputType.SELECT) {
            return (
              <SelectInput
                value={value}
                index={0}
                disabled={disabled}
                fieldData={fieldData}
                valueEditor={valueEditor}
                handleOnChange={handleOnChange}
              />
            );
          } else if (
            el.toUpperCase() == InputType.NUMERIC ||
            dataType?.toUpperCase() === InputType.NUMERIC ||
            dataType?.toUpperCase() === InputType.INTEGER ||
            dataType?.toUpperCase() === InputType.DOUBLE
          ) {
            return (
              <NumericInput
                value={value}
                index={0}
                disabled={disabled}
                fieldData={fieldData}
                valueEditor={valueEditor}
                handleOnChange={handleOnChange}
              />
            );
          } else if (
            el.toUpperCase() === InputType.DATE &&
            dataType === InputType.DATE
          ) {
            if (
              CommaSeparatedSupportedLabels.indexOf(
                valueEditor.label.toUpperCase()
              ) >= 0
            ) {
              return (
                <>
                  <TextInput
                    index={0}
                    value={value}
                    disabled={disabled}
                    fieldData={fieldData}
                    valueEditor={valueEditor}
                    handleOnChange={handleOnChange}
                  />
                </>
              );
            } else {
              return (
                <DateInput
                  value={value}
                  index={0}
                  disabled={disabled}
                  fieldData={fieldData}
                  handleOnChange={handleOnChange}
                  valueEditor={undefined}
                />
              );
            }
          }

          return (
            <TextInput
              index={0}
              value={value}
              disabled={disabled}
              fieldData={fieldData}
              valueEditor={valueEditor}
              handleOnChange={handleOnChange}
            />
          );
        })()
      ) : (
        <TextInput
          index={0}
          value={value}
          disabled={disabled}
          fieldData={fieldData}
          valueEditor={valueEditor}
          handleOnChange={handleOnChange}
        />
      )}
    </React.Fragment>
  );
};

export default CustomValueEditor;
